import { ApiResult } from '../../types';

export type SubmitOutcome = { ok: true } | { ok: false; errors?: any };

type UseSubmitActionDeps = {
  setApiLoading: () => void;
  setApiDone: () => void;
  /** Called once the submit has succeeded, to close the flow down. */
  endCurrent: () => void;
  /**
   * Medical and admin requests branch on the response body's `ok`; the repeat
   * prescription submit ignores it and only fails when the call throws. Left
   * explicit so extraction doesn't silently give one flow the other's handling.
   */
  inspectResult?: boolean;
};

/**
 * The submit wrapper every flow store shares: flag loading, run the call, end
 * the flow on success, translate a rejection into `{ ok: false, errors }`, and
 * always clear the loading flag. Never throws — views rely on that.
 */
export function useSubmitAction(deps: UseSubmitActionDeps) {
  const { inspectResult = true } = deps;

  return async function submitAction(
    call: () => Promise<ApiResult | void>,
  ): Promise<SubmitOutcome> {
    deps.setApiLoading();
    try {
      const result = await call();

      if (inspectResult && result && !result.ok) {
        return { ok: false, errors: result.errors };
      }

      deps.endCurrent();
      return { ok: true };
    } catch (e) {
      return { ok: false, errors: e.data?.errors };
    } finally {
      deps.setApiDone();
    }
  };
}
